/**
 * 📖 Hero Codex — Gallery of hero "boxes" + per-hero skill showcase.
 *
 * Each hero gets a card showing portrait + tagline. Clicking opens a
 * detailed view with the 7 canon skill slots:
 *   Normal · Secondary · Passive · Super Passive · Ultimate · Super Ultimate
 *   · Combo Ultimate
 * Plus combat stats at Lv 1 and Lv 50, the resource model, and lore.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Lock, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 🌳 Codex class_id → Skill Tree hero_id. Only heroes with an authored
// Mind/Body/Soul tree appear here. Extend as trees are authored.
const CODEX_SKILL_TREE_MAP = {
  roc_gym_owner: "roc",
  roc:           "roc",
  david_lauer:   "david",
  david:         "david",
};

const SLOT_META = [
  { key: "normal",         label: "Normal",         icon: "⚡",  hint: "Resource generator. Cheap, spammable." },
  { key: "secondary",      label: "Secondary",      icon: "🎯",  hint: "Other actives — single hits, AoE, shields." },
  { key: "passive",        label: "Passive",        icon: "💢",  hint: "Always-on offensive perk." },
  { key: "super_passive",  label: "Super Passive",  icon: "🛡️", hint: "Always-on defensive perk." },
  { key: "ultimate",       label: "Ultimate",       icon: "🌋",  hint: "Level 1 finisher. Heavy mana cost." },
  { key: "super_ultimate", label: "Super Ultimate", icon: "🔥",  hint: "Level 2 finisher. Empties the bubble." },
  { key: "combo_ultimate", label: "Combo Ultimate", icon: "🌌💥", hint: "Multi-hero synced finisher." },
];

export default function HeroCodexPage() {
  const [heroes, setHeroes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hero-codex`)
      .then((r) => setHeroes(r.data?.heroes || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeId) { setDetail(null); return; }
    axios.get(`${API}/hero-codex/${activeId}`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null));
  }, [activeId]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="hero-codex-page">
        <div className="flex items-center gap-3 mb-6">
          {activeId && (
            <Button
              variant="ghost" size="sm"
              onClick={() => setActiveId(null)}
              data-testid="codex-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> All Heroes
            </Button>
          )}
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-amber-400" />
            Hero Codex
          </h1>
        </div>

        {!activeId ? (
          <HeroGallery heroes={heroes} onPick={setActiveId} />
        ) : detail ? (
          <HeroDetail detail={detail} />
        ) : (
          <div className="text-zinc-500 py-20 text-center" data-testid="codex-loading">Loading codex…</div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function HeroGallery({ heroes, onPick }) {
  if (!heroes.length) {
    return <div className="text-zinc-500 py-20 text-center">No heroes registered yet.</div>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="hero-gallery">
      {heroes.map((h) => (
        <button
          key={h.class_id}
          onClick={() => onPick(h.class_id)}
          className="group text-left bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 hover:border-amber-500/40 rounded-lg overflow-hidden transition-all hover:shadow-[0_0_24px_-8px_rgba(245,158,11,0.5)] hover:-translate-y-0.5"
          data-testid={`hero-card-${h.class_id}`}
        >
          <div className="aspect-square bg-zinc-900/70 flex items-center justify-center relative">
            <img
              src={h.sprite_url}
              alt={h.name}
              className="w-full h-full object-contain p-2 [image-rendering:pixelated] group-hover:scale-105 transition-transform"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            {!h.kit_available && (
              <div className="absolute top-2 right-2 bg-zinc-950/80 px-2 py-0.5 rounded text-[10px] text-zinc-400 flex items-center gap-1">
                <Lock className="w-3 h-3" /> kit pending
              </div>
            )}
          </div>
          <div className="p-3">
            <div className="flex items-center gap-1 text-sm font-bold text-zinc-100">
              <span>{h.icon}</span>
              <span>{h.name}</span>
            </div>
            <div className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{h.tagline}</div>
            <div className="text-[10px] text-amber-400/80 mt-2 uppercase tracking-wide font-mono">
              {h.primary_stat} · {h.secondary_stat}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function HeroDetail({ detail }) {
  return (
    <div className="space-y-6">
      {/* ───── Header card: portrait + stats + resource ───── */}
      <Card className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-zinc-800">
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="aspect-square bg-zinc-950 rounded-lg flex items-center justify-center border border-zinc-800">
            <img
              src={detail.sprite_url}
              alt={detail.name}
              className="w-full h-full object-contain p-4 [image-rendering:pixelated]"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
          <div className="md:col-span-2 space-y-3">
            <div>
              <div className="text-3xl font-bold flex items-center gap-2">
                <span>{detail.icon}</span>{detail.name}
              </div>
              <div className="text-sm text-amber-400 italic">&ldquo;{detail.tagline}&rdquo;</div>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed">{detail.long_desc}</p>

            {/* 🌳 Skill Tree — surfaces per-hero for authored trees only */}
            {CODEX_SKILL_TREE_MAP[detail.class_id || detail.id] && (
              <a
                href={`/skill-tree/${CODEX_SKILL_TREE_MAP[detail.class_id || detail.id]}`}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-emerald-200 text-sm hover:bg-emerald-500/20 transition-colors"
                data-testid="codex-open-skill-tree"
              >
                🌳 <span><b>Skill Tree</b> — Mind · Body · Soul</span>
                <span className="text-xs opacity-70">→</span>
              </a>
            )}

            {detail.lore && (
              <p className="text-xs text-zinc-500 italic border-l-2 border-amber-500/30 pl-3">
                {detail.lore}
              </p>
            )}

            <div className="grid grid-cols-2 gap-3 pt-2">
              <StatBlock title="At Level 1" stats={detail.stats.at_level_1} block={detail.stats.block} charisma={detail.stats.charisma} dominant={detail.stats.dominant} primary={detail.stats.primary_stat} />
              <StatBlock title="At Level 50" stats={detail.stats.at_level_50} block={detail.stats.block} charisma={detail.stats.charisma} dominant={detail.stats.dominant} primary={detail.stats.primary_stat} />
            </div>

            {/* 🌙 Lunar-opposite canon banner */}
            <div
              className={`text-xs flex items-center gap-2 rounded px-3 py-2 border ${
                detail.stats.dominant === "block"
                  ? "bg-sky-500/10 border-sky-500/30 text-sky-300"
                  : detail.stats.dominant === "charisma"
                  ? "bg-rose-500/10 border-rose-500/30 text-rose-300"
                  : "bg-zinc-800 border-zinc-700 text-zinc-300"
              }`}
              data-testid="lunar-opposite-banner"
            >
              <span className="text-base">
                {detail.stats.dominant === "block" ? "🛡️" : detail.stats.dominant === "charisma" ? "✨" : "⚖️"}
              </span>
              <span>
                <strong>{detail.stats.dominant_label}.</strong>{" "}
                <span className="text-zinc-400">
                  Charisma {detail.stats.charisma} vs Block {detail.stats.block} — the higher stat decides the lean. The opposite stat LOWERS the other&apos;s roll but never disables it.
                </span>
              </span>
            </div>

            {detail.resource && (
              <div className="flex items-center gap-2 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 mt-2">
                <span className="text-base">{detail.resource.icon}</span>
                <span><strong className="text-zinc-200">{detail.resource.name}</strong> · cap {detail.resource.cap}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ───── 7 skill slot sections ───── */}
      {SLOT_META.map((meta) => (
        <SkillSection key={meta.key} meta={meta} content={detail.slots[meta.key]} />
      ))}

      {/* ───── 🌌 Legendary Weapon (David's Celestial Spice-Bandolier, etc.) ───── */}
      {detail.legendary_weapon && (
        <LegendaryWeaponCard weapon={detail.legendary_weapon} heroName={detail.name} />
      )}

      {detail.art_credits && (
        <div className="text-[11px] text-zinc-600 italic text-center pt-4 pb-2">
          Art: {detail.art_credits}
        </div>
      )}
    </div>
  );
}

function StatBlock({ title, stats, block, charisma, dominant, primary }) {
  const order = ["patk", "matk", "pdef", "mdef"];
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm font-mono">
        {order.map((k) => (
          <div key={k} className="flex justify-between">
            <span className={`text-zinc-500 ${primary?.toLowerCase() === k ? "text-amber-400" : ""}`}>
              {k.toUpperCase()}
            </span>
            <span className="text-zinc-100">{stats[k]}</span>
          </div>
        ))}
        {typeof block === "number" && (
          <div className={`flex justify-between pt-1 border-t border-zinc-800 mt-1 ${dominant === "block" ? "text-sky-300 font-bold" : ""}`}>
            <span className={dominant === "block" ? "text-sky-400" : "text-zinc-500"}>
              BLOCK {dominant === "block" ? "▲" : ""}
            </span>
            <span>{block}</span>
          </div>
        )}
        {typeof charisma === "number" && (
          <div className={`flex justify-between ${dominant === "charisma" ? "text-rose-300 font-bold" : ""}`}>
            <span className={dominant === "charisma" ? "text-rose-400" : "text-zinc-500"}>
              CHARISMA {dominant === "charisma" ? "▲" : ""}
            </span>
            <span>{charisma}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function SkillSection({ meta, content }) {
  // Normalize to a list — some slots are single, some are arrays.
  const skills = Array.isArray(content) ? content : (content ? [content] : []);
  const isLocked = !skills.length || skills.every((s) => s.locked);

  return (
    <Card className={`bg-zinc-900 border-zinc-800 ${isLocked ? "opacity-70" : ""}`} data-testid={`slot-${meta.key}`}>
      <CardContent className="pt-5">
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-xl">{meta.icon}</span>
          <h3 className="text-lg font-bold tracking-wide">{meta.label}</h3>
          <span className="text-xs text-zinc-500">— {meta.hint}</span>
          {isLocked && (
            <span className="ml-auto text-[10px] uppercase text-zinc-500 flex items-center gap-1">
              <Lock className="w-3 h-3" /> locked
            </span>
          )}
        </div>

        {!skills.length ? (
          <div className="text-sm text-zinc-500 italic py-2">Not authored yet for this hero.</div>
        ) : (
          <div className="space-y-3">
            {skills.map((s) => (
              <SkillRow key={s.id} skill={s} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SkillRow({ skill }) {
  return (
    <div
      className={`bg-zinc-950 border border-zinc-800 rounded p-3 ${skill.locked ? "border-dashed" : ""}`}
      data-testid={`skill-${skill.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl shrink-0">{skill.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-zinc-100">{skill.name}</span>
            {typeof skill.lvl_req === "number" && skill.lvl_req > 1 && (
              <span
                className="text-[10px] uppercase font-mono bg-amber-500/15 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded"
                title="Unlocks at this hero level — items/quests may override."
              >
                Lv {skill.lvl_req}
              </span>
            )}
            {skill.kind && (
              <span className="text-[10px] uppercase bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">
                {skill.kind}
              </span>
            )}
            {skill.category && (
              <span className="text-[10px] uppercase bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">
                {skill.category}
              </span>
            )}
            {typeof skill.cost === "number" && skill.cost > 0 && (
              <span className="text-[10px] text-blue-400 font-mono">cost {skill.cost}</span>
            )}
            {typeof skill.gain === "number" && skill.gain > 0 && (
              <span className="text-[10px] text-emerald-400 font-mono">+{skill.gain} mana</span>
            )}
            {skill.locked && (
              <span className="ml-auto text-[10px] uppercase text-amber-500/80">coming soon</span>
            )}
          </div>
          {skill.tagline && <div className="text-sm text-amber-400/80 mt-1 italic">{skill.tagline}</div>}
          {skill.long_desc && <p className="text-sm text-zinc-300 mt-1">{skill.long_desc}</p>}

          {/* Passive tier ladder */}
          {Array.isArray(skill.tiers) && skill.tiers.length > 0 && (
            <ul className="text-xs text-zinc-400 mt-2 space-y-0.5">
              {skill.tiers.map((t) => (
                <li key={t.tier} className="flex gap-2">
                  <span className="text-amber-500 font-mono">T{t.tier}</span>
                  <span>{t.bonus}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Effects list */}
          {Array.isArray(skill.effects) && skill.effects.length > 0 && (
            <div className="text-xs text-zinc-500 mt-2 font-mono">
              {skill.effects.map((e, i) => (
                <span key={i} className="inline-block mr-2">
                  {e.kind}{e.pct ? ` ${e.pct}%` : ""}{e.turns ? ` · ${e.turns}t` : ""}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ═════════════════ 🌌 LEGENDARY WEAPON CARD ═════════════════
 * Renders when a hero has a canonized legendary_weapon in their kit.
 * Currently only David Lauer's Celestial Spice-Bandolier — designed to
 * accept future entries for other heroes.
 */
function LegendaryWeaponCard({ weapon, heroName }) {
  if (!weapon) return null;
  return (
    <div
      className="mt-6 rounded-2xl border-2 border-purple-700/50 bg-gradient-to-br from-[#0a0510] via-[#0d0620] to-[#12061e] p-5 shadow-[inset_0_0_60px_rgba(147,51,234,0.18)]"
      data-testid={`legendary-weapon-${weapon.id}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-[0.3em] text-purple-300 font-bold">
          Legendary Weapon · Cosmic Ultimate
        </span>
      </div>

      <div className="flex items-start gap-4">
        <div className="text-6xl leading-none drop-shadow-[0_0_20px_rgba(147,51,234,0.6)]">
          {weapon.icon}
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-purple-400">
            {heroName}&apos;s Cosmic Ultimate Weapon
          </div>
          <div className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            {weapon.name}
          </div>
          <div className="text-xs text-purple-300/80 italic mt-1">
            {weapon.tagline}
          </div>
          {weapon.base_form && (
            <div className="text-[11px] text-zinc-500 mt-1">
              Base form: <span className="text-zinc-300">{weapon.base_form}</span>
            </div>
          )}
        </div>
      </div>

      {weapon.visual && (
        <div className="mt-4 text-[12px] text-zinc-300 leading-relaxed border-l-2 border-purple-700/40 pl-3 italic">
          {weapon.visual}
        </div>
      )}

      {weapon.narrative && (
        <div className="mt-3 text-[12px] text-zinc-400 leading-relaxed">
          {weapon.narrative}
        </div>
      )}

      {/* Passive */}
      {weapon.passive && (
        <div className="mt-4 rounded-lg border border-purple-700/40 bg-black/40 p-3">
          <div className="text-[10px] uppercase tracking-widest text-purple-300">Passive</div>
          <div className="text-sm font-bold text-white mt-1">{weapon.passive.name}</div>
          <div className="text-[11px] text-zinc-300 mt-1 leading-relaxed">{weapon.passive.effect}</div>
          {weapon.passive.legacy_bonus && (
            <div className="text-[11px] text-amber-300 mt-2 italic border-l-2 border-amber-500/50 pl-2">
              +{weapon.passive.legacy_bonus}
            </div>
          )}
        </div>
      )}

      {/* Active */}
      {weapon.active && (
        <div className="mt-3 rounded-lg border border-purple-700/40 bg-black/40 p-3">
          <div className="text-[10px] uppercase tracking-widest text-purple-300">Active</div>
          <div className="text-sm font-bold text-white mt-1">{weapon.active.name}</div>
          {weapon.active.flavor && (
            <div className="text-[11px] text-zinc-400 italic mt-1">{weapon.active.flavor}</div>
          )}
          {Array.isArray(weapon.active.options) && (
            <div className="mt-2 space-y-2">
              {weapon.active.options.map((opt) => (
                <div key={opt.id} className="rounded border border-zinc-800 bg-black/50 p-2">
                  <div className="text-xs font-bold text-purple-200">{opt.name}</div>
                  <div className="text-[11px] text-zinc-300 mt-0.5">{opt.effect}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Ultimate Trait */}
      {weapon.ultimate_trait && (
        <div className="mt-3 rounded-lg border-2 border-purple-500/50 bg-purple-900/20 p-3">
          <div className="text-[10px] uppercase tracking-widest text-purple-200 font-bold">
            Ultimate Trait · The Combo Catalyst
          </div>
          <div className="text-sm font-bold text-white mt-1">{weapon.ultimate_trait.name}</div>
          <div className="text-[11px] text-zinc-200 mt-1 leading-relaxed">{weapon.ultimate_trait.mechanism}</div>
        </div>
      )}

      {/* Global gate notice */}
      {weapon.gates_combo_ultimates_globally && (
        <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-900/10 p-3">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
            🔒 Roster-Wide Gate
          </div>
          <div className="text-[11px] text-zinc-300 mt-1 leading-relaxed">
            Every Combo Ultimate involving {heroName} across the entire roster remains
            <strong className="text-amber-200"> LOCKED</strong> until this weapon is forged. {heroName} is
            the network hub for the Combo Ultimate system — one quest completion cascades the unlock across
            every partner hero.
          </div>
        </div>
      )}

      {/* Quest */}
      {weapon.quest && (
        <div className="mt-3 rounded-lg border border-zinc-800 bg-black/40 p-3">
          <div className="text-[10px] uppercase tracking-widest text-purple-300">
            Cosmic Weapon Quest · &ldquo;{weapon.quest.name}&rdquo;
          </div>
          <ol className="mt-2 space-y-1.5 text-[11px]">
            {(weapon.quest.phases || []).map((p) => (
              <li key={p.phase} className="flex gap-2">
                <span className="shrink-0 w-5 h-5 rounded-full bg-purple-500/30 text-purple-200 text-[10px] font-bold flex items-center justify-center">
                  {p.phase}
                </span>
                <div>
                  <div className="text-purple-200 font-bold">{p.title}</div>
                  <div className="text-zinc-400 leading-snug">{p.summary}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
